import { createNavigation } from 'next-intl/navigation';
import { defineRouting } from 'next-intl/routing';
import { i18nConfig } from '../project.config';
import { localeDomainHosts } from './domains';

// EWM-522 — next-intl owns locale routing (proxy() delegates to
// createMiddleware(routing); see src/proxy.ts). `localePrefix` is ENV-CONDITIONAL:
//
//   • domains configured (NEXT_PUBLIC_HOST_AU/NZ set — deployed envs, or 2 ngrok
//     hosts in dev) → 'never'. The locale NEVER appears in a public URL on any
//     host; it's resolved from the matched host (else cookie/default) and used
//     only for the internal [locale] segment + region. A /{locale}/ request is
//     301'd to the clean URL. The env-gated `domains[]` is purely the host→locale
//     map (electus-au→en-AU, electus-nz→en-NZ); unmatched hosts serve the default
//     locale, also clean.
//   • not configured (plain localhost) → 'always': the current /{locale}/ URLs,
//     unchanged.
//
// Because the top-level mode matches the active state, next-intl's client nav and
// the middleware always agree (no prefix-on-client / clean-on-server mismatch).
// Hosts come from the single source in src/i18n/domains.ts (shared with the SEO
// emitters). `localeCookie` ('locale') lets a non-domain host select a non-default
// locale — the ship-and-language switcher sets it (preserving the old contract).
//
// EWM-862 — a locale can be served on SEVERAL hosts (canonical `www.`, the bare
// apex, a legacy `shop.`), so each locale contributes one `domains[]` entry PER
// configured host. A host that's absent from this map is UNMATCHED, and an
// unmatched host falls back to `defaultLocale` (en-AU) — which is how the bare
// `electusdistribution.co.nz` came to send NZ visitors to the AU storefront in
// AUD after the www cutover. Every host that can reach the app must be listed.
const AU_HOSTS = localeDomainHosts('en-AU');
const NZ_HOSTS = localeDomainHosts('en-NZ');
// Canonical host per locale (first entry) — decides whether domain mode is on.
const AU_HOST = AU_HOSTS[0];
const NZ_HOST = NZ_HOSTS[0];

export const routing = defineRouting({
  locales: i18nConfig.locales,
  defaultLocale: i18nConfig.defaultLocale,
  localePrefix: AU_HOST && NZ_HOST ? 'never' : 'always',
  localeCookie: { name: 'locale', maxAge: 60 * 60 * 24 * 7 * 4 * 12 * 100 },
  // Host→locale map; each domain inherits the top-level 'never' prefix. One entry
  // per configured host, so every alias resolves to its OWN country's locale.
  ...(AU_HOST && NZ_HOST
    ? {
        domains: [
          ...AU_HOSTS.map((domain) => ({ domain, defaultLocale: 'en-AU' as const, locales: ['en-AU' as const] })),
          ...NZ_HOSTS.map((domain) => ({ domain, defaultLocale: 'en-NZ' as const, locales: ['en-NZ' as const] })),
        ],
      }
    : {}),
});

// Lightweight wrappers around Next.js' navigation APIs
// that will consider the routing configuration
export const { Link, redirect, usePathname, useRouter, getPathname } = createNavigation(routing);
